/**
 * RTCRtpScriptTransform Worker for Animation Track (VP8 Video)
 *
 * This worker handles extracting animation data from VP8 video frames.
 * The server prepends a minimal VP8 header to trick the browser's depacketizer.
 *
 * Frame format after depacketization (VP8 descriptor is stripped by depacketizer):
 * [0-9]     VP8 frame header (10 bytes) - minimal keyframe structure, skipped
 * [10]      flags (uint8) - packet flags
 * [11-14]   msgLen (little-endian u32) - length of protobuf message
 * [15..15+msgLen)  protobuf Message binary
 */

// Type declarations for RTCRtpScriptTransform API (experimental)
declare interface RTCTransformEvent extends Event {
  transformer: {
    readable: ReadableStream;
    writable: WritableStream;
    options?: unknown;
  };
}

// Worker global scope with RTCRtpScriptTransform support
declare const self: DedicatedWorkerGlobalScope & {
  onrtctransform?: ((event: RTCTransformEvent) => void) | null;
};

// VP8 header size (frame tag + sync code + dimensions, descriptor already stripped)
const VP8_FRAME_HEADER_SIZE = 10;

// Protocol constants (must match egress-server/metadata_track.go)
const METADATA_FIXED_HEADER_SIZE = 5; // 1B flags + 4B length

// Packet flags
const PACKET_FLAG_IDLE = 0x01;
const PACKET_FLAG_START = 0x02;
const PACKET_FLAG_END = 0x04;
const PACKET_FLAG_GZIPPED = 0x08;
const PACKET_FLAG_TRANSITION = 0x10; // Transition packet - contains target frame for generating transition from idle
const PACKET_FLAG_TRANSITION_END = 0x20; // Transition end packet - contains last frame for generating transition back to idle
const PACKET_FLAG_REDUNDANT = 0x40; // Packet contains redundant previous frame for ALR loss recovery

interface FrameMetadata {
  flags: number;
  protobufLength: number;
  protobufData: Uint8Array;
  // ALR (Application-Level Redundancy) data
  hasRedundant: boolean;
  redundantLength: number;
  redundantData: Uint8Array | null;
}

interface TransformOptions {
  operation: 'receiver';
}

// State for receiver
let receiverMetaCount = 0;
let lastLogTime = 0;
let totalFrames = 0;
let framesWithMeta = 0;
let wasIdle = false; // Track idle state for transition detection
let isInStartTransition = false; // Dedup consecutive transition-start packets
let isInEndTransition = false; // Dedup consecutive transition-end packets

// ALR (Application-Level Redundancy) state for loss detection
let lastReceivedTimestamp: number | null = null;
let framesRecovered = 0;
let framesLost = 0;
// Expected timestamp increment per animation frame (90000 Hz * 40ms = 3600)
// Animation is sent every 2nd audio frame (40ms intervals)
const EXPECTED_TIMESTAMP_INCREMENT = 3600;

// Sequence tracking for out-of-order detection
let lastRenderedSeq: number = -1; // Last sequence number sent to main thread
let framesOutOfOrder = 0; // Count of out-of-order frames detected
let framesDuplicate = 0; // Count of duplicate frames skipped
let framesDropped = 0; // Count of frames that were permanently lost (gaps in sequence)
let framesSent = 0; // Total frames successfully sent to main thread

function parseMetadataHeader(
  data: Uint8Array,
): { meta: FrameMetadata; headerSize: number } | null {
  if (data.byteLength < METADATA_FIXED_HEADER_SIZE) return null;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const flags = data[0];
  const msgLen = view.getUint32(1, true); // little-endian

  // Calculate minimum size needed
  const headerSize = METADATA_FIXED_HEADER_SIZE + msgLen;
  if (data.byteLength < headerSize) return null;

  // The compressed payload (will be decompressed later)
  const compressedData = data.slice(
    METADATA_FIXED_HEADER_SIZE,
    METADATA_FIXED_HEADER_SIZE + msgLen,
  );

  // Note: With the new ALR optimization, both frames are gzipped together
  // The format INSIDE the gzip (after decompression) is:
  // - ALR: [currentLen (4B)][currentData][prevLen (4B)][prevData]
  // - Non-ALR: just the raw protobuf data
  // We return the compressed data here; decompression and ALR parsing happens in receiverTransform

  const hasRedundant = (flags & PACKET_FLAG_REDUNDANT) !== 0;

  return {
    meta: {
      flags,
      protobufLength: msgLen,
      protobufData: compressedData, // This is still compressed at this point
      hasRedundant,
      redundantLength: 0, // Will be determined after decompression
      redundantData: null, // Will be extracted after decompression
    },
    headerSize,
  };
}

function isIdlePacket(flags: number): boolean {
  return (flags & PACKET_FLAG_IDLE) !== 0;
}

function isStartPacket(flags: number): boolean {
  return (flags & PACKET_FLAG_START) !== 0;
}

function isEndPacket(flags: number): boolean {
  return (flags & PACKET_FLAG_END) !== 0;
}

function isGzipped(flags: number): boolean {
  return (flags & PACKET_FLAG_GZIPPED) !== 0;
}

function isTransitionPacket(flags: number): boolean {
  return (flags & PACKET_FLAG_TRANSITION) !== 0;
}

function isTransitionEndPacket(flags: number): boolean {
  return (flags & PACKET_FLAG_TRANSITION_END) !== 0;
}

// Decompress gzipped data using DecompressionStream API
async function decompressGzip(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  // Create a copy to ensure we have a plain ArrayBuffer, not SharedArrayBuffer
  const copy = new Uint8Array(data);
  writer.write(copy);
  writer.close();

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    totalLength += value.length;
  }

  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result;
}

// Compression stats for logging
let totalCompressedBytes = 0;
let totalUncompressedBytes = 0;

// ALR payload structure after decompression
interface ALRPayload {
  frameSeq: number; // Sequence number of current frame
  currentData: Uint8Array; // Current frame (N)
  prev1Data: Uint8Array | null; // Previous frame (N-1)
  prev2Data: Uint8Array | null; // Frame before previous (N-2)
}

// Parse ALR payload after decompression
// ALR format: [frameSeq (4B)][currentLen (4B)][currentData][prev1Len (4B)][prev1Data][prev2Len (4B)][prev2Data]
// Non-ALR format: [frameSeq (4B)][raw protobuf data]
// Returns { frameSeq, currentData, prev1Data, prev2Data } or null if parsing fails
function parseALRPayload(
  decompressed: Uint8Array,
  hasRedundant: boolean,
): ALRPayload | null {
  if (decompressed.byteLength < 4) return null;

  const view = new DataView(
    decompressed.buffer,
    decompressed.byteOffset,
    decompressed.byteLength,
  );
  let offset = 0;

  // Parse frame sequence number
  const frameSeq = view.getUint32(offset, true);
  offset += 4;

  if (!hasRedundant) {
    // Non-ALR: rest is raw protobuf
    const currentData = decompressed.slice(offset);
    return { frameSeq, currentData, prev1Data: null, prev2Data: null };
  }

  // ALR format: parse current frame
  if (decompressed.byteLength < offset + 4) return null;
  const currentLen = view.getUint32(offset, true);
  offset += 4;
  if (decompressed.byteLength < offset + currentLen) return null;
  const currentData = decompressed.slice(offset, offset + currentLen);
  offset += currentLen;

  // Parse prev1 (N-1) if available
  let prev1Data: Uint8Array | null = null;
  if (decompressed.byteLength >= offset + 4) {
    const prev1Len = view.getUint32(offset, true);
    offset += 4;
    if (prev1Len > 0 && decompressed.byteLength >= offset + prev1Len) {
      prev1Data = decompressed.slice(offset, offset + prev1Len);
      offset += prev1Len;
    }
  }

  // Parse prev2 (N-2) if available
  let prev2Data: Uint8Array | null = null;
  if (decompressed.byteLength >= offset + 4) {
    const prev2Len = view.getUint32(offset, true);
    offset += 4;
    if (prev2Len > 0 && decompressed.byteLength >= offset + prev2Len) {
      prev2Data = decompressed.slice(offset, offset + prev2Len);
    }
  }

  return { frameSeq, currentData, prev1Data, prev2Data };
}

// Helper to send animation data to main thread with sequence tracking
// Returns true if frame was sent, false if skipped (duplicate/out-of-order)
function sendAnimationToMainThread(
  protobufData: Uint8Array,
  flags: number,
  frameSeq: number,
  isRecovered: boolean = false,
): boolean {
  const isIdle = isIdlePacket(flags);
  const isStart = isStartPacket(flags);
  const isEnd = isEndPacket(flags);

  // Check for out-of-order or duplicate frames
  if (frameSeq <= lastRenderedSeq && lastRenderedSeq !== -1 && !isStart) {
    if (frameSeq === lastRenderedSeq) {
      framesDuplicate++;
    } else {
      framesOutOfOrder++;
    }
    return false;
  }

  // Check for gap (frames we never received and couldn't recover) - these are DROPPED
  if (lastRenderedSeq !== -1 && frameSeq > lastRenderedSeq + 1 && !isStart) {
    const gap = frameSeq - lastRenderedSeq - 1;
    framesDropped += gap;
  }

  framesSent++;

  lastRenderedSeq = frameSeq;

  const protobufBuffer = new ArrayBuffer(protobufData.byteLength);
  new Uint8Array(protobufBuffer).set(protobufData);

  self.postMessage(
    {
      type: 'animation',
      flags,
      isIdle,
      isStart,
      isEnd,
      isRecovered,
      frameSeq,
      protobufData: protobufBuffer,
    },
    { transfer: [protobufBuffer] },
  );

  return true;
}

// Receiver transform: extract animation data from video frames
// Uses ALR (Application-Level Redundancy) to recover lost frames
function receiverTransform(
  frame: RTCEncodedVideoFrame,
  _controller: TransformStreamDefaultController<RTCEncodedVideoFrame>,
) {
  totalFrames++;
  const data = new Uint8Array(frame.data);
  const currentTimestamp = frame.timestamp;

  // Skip the VP8 frame header (10 bytes) - the VP8 descriptor is already stripped by depacketizer
  // Check if we have enough data for VP8 header + at least 1 byte of animation data
  if (data.length <= VP8_FRAME_HEADER_SIZE) {
    return; // Frame too small, skip
  }

  // Extract animation data after VP8 header
  const animData = data.subarray(VP8_FRAME_HEADER_SIZE);

  const parsed = parseMetadataHeader(animData);

  if (parsed) {
    const { meta } = parsed;
    framesWithMeta++;
    receiverMetaCount++;

    const isIdle = isIdlePacket(meta.flags);
    const isStart = isStartPacket(meta.flags);
    const isEnd = isEndPacket(meta.flags);

    // ALR: Detect frame loss and recover using redundant data
    // Check if we missed frames by looking at timestamp gap
    if (lastReceivedTimestamp !== null && !isIdle && !isStart) {
      const timestampDelta = currentTimestamp - lastReceivedTimestamp;
      // Allow some tolerance (1.5x expected increment) for timing variations
      if (timestampDelta > EXPECTED_TIMESTAMP_INCREMENT * 1.5) {
        const missedFrames =
          Math.round(timestampDelta / EXPECTED_TIMESTAMP_INCREMENT) - 1;
        framesLost += missedFrames;

        // Try to recover using redundant data from current packet
        // With ALR optimization, up to 3 frames are gzipped together
        if (meta.hasRedundant && isGzipped(meta.flags)) {
          // Decompress to get all frames, then recover
          totalCompressedBytes += meta.protobufData.byteLength;

          decompressGzip(meta.protobufData)
            .then((decompressed) => {
              totalUncompressedBytes += decompressed.byteLength;

              const parsed = parseALRPayload(decompressed, true);
              if (parsed) {
                const currentSeq = parsed.frameSeq;

                // Determine how many frames we can recover based on how many were lost
                // and how many redundant frames we have
                const framesToRecover: Array<{
                  data: Uint8Array;
                  seq: number;
                }> = [];

                if (missedFrames >= 2 && parsed.prev2Data) {
                  framesToRecover.push({
                    data: parsed.prev2Data,
                    seq: currentSeq - 2,
                  });
                }
                if (missedFrames >= 1 && parsed.prev1Data) {
                  framesToRecover.push({
                    data: parsed.prev1Data,
                    seq: currentSeq - 1,
                  });
                }

                const recovered = framesToRecover.length;
                if (recovered > 0) {
                  framesRecovered += recovered;

                  // Send recovered frames in chronological order (oldest first)
                  for (const frame of framesToRecover) {
                    sendAnimationToMainThread(
                      frame.data,
                      meta.flags & ~PACKET_FLAG_REDUNDANT,
                      frame.seq,
                      true,
                    );
                  }
                }

                // Then send current frame
                sendAnimationToMainThread(
                  parsed.currentData,
                  meta.flags & ~PACKET_FLAG_REDUNDANT,
                  currentSeq,
                  false,
                );
              }
            })
            .catch((err) => {
              console.error(`[Animation Worker] ALR decompression error:`, err);
            });

          // Update timestamp and return early (we've handled this frame)
          lastReceivedTimestamp = currentTimestamp;
          return;
        }
      }
    }

    // Update last received timestamp (skip for idle packets to avoid false loss detection)
    if (!isIdle) {
      lastReceivedTimestamp = currentTimestamp;
    }

    // Reset state on session start
    if (isStart) {
      lastReceivedTimestamp = currentTimestamp;
      framesRecovered = 0;
      framesLost = 0;
      lastRenderedSeq = -1;
      framesOutOfOrder = 0;
      framesDuplicate = 0;
      framesDropped = 0;
      framesSent = 0;
    }

    const isTransition = isTransitionPacket(meta.flags);
    const isTransitionEnd = isTransitionEndPacket(meta.flags);

    if (isIdle) {
      // First idle packet - post idleStart event
      if (!wasIdle) {
        self.postMessage({ type: 'idleStart' });
        wasIdle = true;
      }
      isInStartTransition = false;
      isInEndTransition = false;
    } else if (isTransition && meta.protobufLength > 0) {
      // Transition packet - contains target frame for smooth transition from idle
      wasIdle = false;

      // Dedup: only emit first packet in a consecutive transition-start run.
      if (!isInStartTransition) {
        isInStartTransition = true;
        isInEndTransition = false;

        // Decompress if gzipped (transition packets are always gzipped)
        const gzipped = isGzipped(meta.flags);
        if (gzipped) {
          totalCompressedBytes += meta.protobufData.byteLength;

          decompressGzip(meta.protobufData)
            .then((decompressed) => {
              totalUncompressedBytes += decompressed.byteLength;

              const protobufBuffer = new ArrayBuffer(decompressed.byteLength);
              new Uint8Array(protobufBuffer).set(decompressed);

              // Post transition event - main thread will generate transition frames
              self.postMessage(
                {
                  type: 'transition',
                  flags: meta.flags,
                  protobufData: protobufBuffer,
                },
                { transfer: [protobufBuffer] },
              );
            })
            .catch((err) => {
              console.error(
                `[Animation Worker] Gzip decompress error (transition):`,
                err,
              );
            });
        } else {
          const protobufBuffer = new ArrayBuffer(meta.protobufData.byteLength);
          new Uint8Array(protobufBuffer).set(meta.protobufData);

          self.postMessage(
            {
              type: 'transition',
              flags: meta.flags,
              protobufData: protobufBuffer,
            },
            { transfer: [protobufBuffer] },
          );
        }
      }
    } else if (isTransitionEnd && meta.protobufLength > 0) {
      // Transition end packet - contains last frame for smooth transition back to idle

      // Dedup: only emit first packet in a consecutive transition-end run.
      if (!isInEndTransition) {
        isInEndTransition = true;
        isInStartTransition = false;

        // Decompress if gzipped (transition end packets are always gzipped)
        const gzipped = isGzipped(meta.flags);
        if (gzipped) {
          totalCompressedBytes += meta.protobufData.byteLength;

          decompressGzip(meta.protobufData)
            .then((decompressed) => {
              totalUncompressedBytes += decompressed.byteLength;

              const protobufBuffer = new ArrayBuffer(decompressed.byteLength);
              new Uint8Array(protobufBuffer).set(decompressed);

              // Post transition end event - main thread will generate reverse transition frames
              self.postMessage(
                {
                  type: 'transitionEnd',
                  flags: meta.flags,
                  protobufData: protobufBuffer,
                },
                { transfer: [protobufBuffer] },
              );
            })
            .catch((err) => {
              console.error(
                `[Animation Worker] Gzip decompress error (transitionEnd):`,
                err,
              );
            });
        } else {
          const protobufBuffer = new ArrayBuffer(meta.protobufData.byteLength);
          new Uint8Array(protobufBuffer).set(meta.protobufData);

          self.postMessage(
            {
              type: 'transitionEnd',
              flags: meta.flags,
              protobufData: protobufBuffer,
            },
            { transfer: [protobufBuffer] },
          );
        }
      }
    } else if (meta.protobufLength > 0) {
      // Normal animation packet
      // Transition from idle to streaming (if not already transitioned via transition packet)
      if (wasIdle) {
        wasIdle = false;
      }
      isInStartTransition = false;
      isInEndTransition = false;

      // Decompress if gzipped
      const gzipped = isGzipped(meta.flags);
      if (gzipped) {
        // Track compression stats
        totalCompressedBytes += meta.protobufData.byteLength;

        // Async decompression
        decompressGzip(meta.protobufData)
          .then((decompressed) => {
            totalUncompressedBytes += decompressed.byteLength;

            // Parse ALR format to get frame sequence and data
            // Format: [frameSeq (4B)][currentLen (4B)][currentData]... or [frameSeq (4B)][raw protobuf]
            const parsed = parseALRPayload(decompressed, meta.hasRedundant);
            if (parsed) {
              sendAnimationToMainThread(
                parsed.currentData,
                meta.flags & ~PACKET_FLAG_REDUNDANT,
                parsed.frameSeq,
                false,
              );
            }
          })
          .catch(() => {});
      } else {
        // Uncompressed - this shouldn't happen in normal operation but handle it
        const protobufBuffer = new ArrayBuffer(meta.protobufData.byteLength);
        new Uint8Array(protobufBuffer).set(meta.protobufData);

        self.postMessage(
          {
            type: 'animation',
            flags: meta.flags,
            isIdle: false,
            isStart,
            isEnd,
            frameSeq: -1, // Unknown sequence
            protobufData: protobufBuffer,
          },
          { transfer: [protobufBuffer] },
        );
      }
    }

    // Report stats every second
    const now = performance.now();
    if (now - lastLogTime > 1000) {
      lastLogTime = now;
      self.postMessage({
        type: 'metadata',
        protobufLength: meta.protobufLength,
        framesPerSec: receiverMetaCount,
        totalFrames,
        framesWithMeta,
        framesLost,
        framesRecovered,
        totalCompressedBytes,
        totalUncompressedBytes,
        framesOutOfOrder,
        framesDuplicate,
        framesDropped,
        framesSent,
        lastRenderedSeq,
      });
      receiverMetaCount = 0;
    }
  }

  // For video, we don't enqueue the frame since it's not real video
  // The browser's VP8 decoder would fail on our custom data anyway
  // controller.enqueue(frame);
}

// Handle rtctransform event (RTCRtpScriptTransform)
self.onrtctransform = (event: RTCTransformEvent) => {
  const transformer = event.transformer;
  const options = transformer.options as TransformOptions;

  try {
    if (options.operation === 'receiver') {
      // Reset state
      receiverMetaCount = 0;
      lastLogTime = 0;
      totalFrames = 0;
      framesWithMeta = 0;
      wasIdle = false;
      isInStartTransition = false;
      isInEndTransition = false;
      lastReceivedTimestamp = null;
      framesRecovered = 0;
      framesLost = 0;
      lastRenderedSeq = -1;
      framesOutOfOrder = 0;
      framesDuplicate = 0;
      framesDropped = 0;
      framesSent = 0;

      transformer.readable
        .pipeThrough(new TransformStream({ transform: receiverTransform }))
        .pipeTo(transformer.writable)
        .catch((err: unknown) => {
          console.error('[Animation Worker] Pipeline error:', err);
          self.postMessage({
            type: 'error',
            error: `Animation receiver pipe error: ${err}`,
          });
        });

      self.postMessage({ type: 'ready', operation: 'receiver' });
    }
  } catch (err) {
    console.error('[Animation Worker] Setup error:', err);
    self.postMessage({
      type: 'error',
      error: `Animation transform setup error: ${err}`,
    });
  }
};

// Handle message-based initialization for fallback
self.onmessage = (event: MessageEvent) => {
  const { type } = event.data;
  if (type === 'init') {
    self.postMessage({ type: 'initialized' });
  }
};

export {};
